import { Paragraph } from '@tiptap/extension-paragraph'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { blockMenuStore, figureMenuStore } from '../../stores/menuStore'
import { get } from 'svelte/store'

// Skeleton Loader NodeView
class SkeletonLoaderNodeView {
    constructor(node, view, getPos) {
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        
        // Create the DOM element
        this.dom = document.createElement('div');
        this.dom.className = 'skeleton-loader-node-view';
        this.dom.setAttribute('data-context', node.attrs.context || '');
        this.dom.setAttribute('data-skeleton-index', node.attrs.skeletonIndex || '0');
        
        this.createContent();
        this.ensureStyles();
    }
    
    createContent() {
        this.dom.innerHTML = `
            <div class="skeleton-loader-wrapper">
                <div class="skeleton-line line-1"></div>
                <div class="skeleton-line line-2"></div>
                <div class="skeleton-line line-3"></div>
                <div class="skeleton-line line-4"></div>
            </div>
        `;
    }
    
    ensureStyles() {
        if (!document.head.querySelector('#skeleton-nodeview-styles')) {
            const style = document.createElement('style');
            style.id = 'skeleton-nodeview-styles';
            style.textContent = `
                .skeleton-loader-node-view {
                    position: relative;
                    padding: 0.5em 0.5em 0.5em 1em;
                    margin: 0.5em 0;
                    background-color: var(--color-skeleton-bg, rgba(212, 84, 138, 0.06));
                    border-radius: 4px;
                    border-left: 3px solid var(--color-skeleton-border, rgba(212, 84, 138, 0.3));
                    min-height: 5em;
                    overflow: hidden;
                    pointer-events: none;
                    user-select: none;
                    animation: skeletonNodePulse 1.5s ease-in-out infinite;
                }
                
                .skeleton-loader-wrapper {
                    position: relative;
                }
                
                .skeleton-line {
                    height: 0.9em;
                    margin: 0.5em 0;
                    background: var(--color-skeleton-line, rgba(212, 84, 138, 0.15));
                    border-radius: 3px;
                    position: relative;
                    overflow: hidden;
                }
                
                .skeleton-line.line-1 { width: 90%; }
                .skeleton-line.line-2 { width: 85%; }
                .skeleton-line.line-3 { width: 95%; }
                .skeleton-line.line-4 { width: 80%; }
                
                .skeleton-line::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(
                        90deg,
                        transparent,
                        var(--color-skeleton-shimmer, rgba(255, 255, 255, 0.4)),
                        transparent
                    );
                    animation: skeletonNodeShimmer 2s linear infinite;
                }
                
                @keyframes skeletonNodePulse {
                    0%, 100% { opacity: 0.7; }
                    50% { opacity: 1; }
                }
                
                @keyframes skeletonNodeShimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(300%); }
                }
            `;
            document.head.appendChild(style);
        }
    }
    
    update(node) {
        if (node.type !== this.node.type) return false;
        if (node.attrs.blockType !== 'skeleton-loader') return false;
        this.node = node;
        return true;
    }
    
    destroy() {
        this.dom?.remove();
    }
    
    // Allow mutations so the node can be properly replaced
    ignoreMutation() {
        return false;
    }
    
    // Mark as non-selectable to allow easy replacement
    get selectNode() {
        return false;
    }
}

const paragraphNumberingPluginKey = new PluginKey('paragraphNumbering')

export const CustomParagraph = Paragraph.extend({
    name: 'paragraph',

    addAttributes() {
        return {
            class: {
                default: 'editor-paragraph',
                parseHTML: element => element.getAttribute('class'),
                renderHTML: attributes => {
                    return {
                        class: attributes.class
                    }
                }
            },
            blockType: {
                default: null,
                parseHTML: element => element.getAttribute('data-block-type'),
                renderHTML: attributes => {
                    if (!attributes.blockType) {
                        return {}
                    }
                    
                    const attrs = {
                        'data-block-type': attributes.blockType
                    }
                    
                    // Add pre-wrap style for regenerative blocks to preserve whitespace
                    if (attributes.blockType === 'regenerative-support') {
                        attrs.style = 'white-space: pre-wrap; min-height: 1em;'
                    }
                    
                    return attrs
                }
            },
            finalContent: {
                default: null,
                parseHTML: element => element.getAttribute('data-final-content'),
                renderHTML: attributes => {
                    if (!attributes.finalContent) {
                        return {}
                    }
                    return {
                        'data-final-content': attributes.finalContent
                    }
                }
            },
            context: {
                default: null,
                parseHTML: element => element.getAttribute('data-context'),
                renderHTML: attributes => {
                    if (!attributes.context) {
                        return {}
                    }
                    return {
                        'data-context': attributes.context
                    }
                }
            },
            name: {
                default: null,
                parseHTML: element => element.getAttribute('data-name'),
                renderHTML: attributes => {
                    if (!attributes.name) {
                        return {}
                    }
                    return {
                        'data-name': attributes.name
                    }
                }
            },
            claim: {
                default: null,
                parseHTML: element => element.getAttribute('data-claim'),
                renderHTML: attributes => {
                    if (!attributes.claim) {
                        return {}
                    }
                    return {
                        'data-claim': attributes.claim
                    }
                }
            },
            skeletonIndex: {
                default: null,
                parseHTML: element => {
                    const index = element.getAttribute('data-skeleton-index');
                    return index !== null ? parseInt(index, 10) : null;
                },
                renderHTML: attributes => {
                    if (attributes.skeletonIndex === null || attributes.skeletonIndex === undefined) {
                        return {}
                    }
                    return {
                        'data-skeleton-index': attributes.skeletonIndex
                    }
                }
            }
        }
    },

    addNodeView() {
        return ({ node, view, getPos, decorations }) => {
            // Only use NodeView for skeleton-loader blocks
            if (node.attrs.blockType === 'skeleton-loader') {
                return new SkeletonLoaderNodeView(node, view, getPos);
            }
            // Return null for other blocks to use default rendering
            return null;
        };
    },
    

    addKeyboardShortcuts() {

        return {
            Enter: ({ editor }) => {
                
                // First check if any menus are open - if so, don't handle Enter here
                const blockMenuState = get(blockMenuStore);
                const figureMenuState = get(figureMenuStore);
                
                if (blockMenuState.isOpen || figureMenuState.isOpen) {
                    // Completely stop the enter key from doing anything when menu is open
                    // This prevents unexpected newline creation during menu interaction
                    return true;
                }
                
                // Check if we're in a regenerative support or boilerplate block
                const attrs = editor.getAttributes('paragraph');
                
                if (attrs.blockType === 'regenerative-support') {
                    // Insert a new paragraph with the same regenerative attributes
                    // instead of a hard break to enable paragraph numbering
                    const result = editor
                        .chain()
                        .insertContent({
                            type: 'paragraph',
                            attrs: {
                                class: 'editor-paragraph',
                                blockType: 'regenerative-support',
                                context: attrs.context,
                                finalContent: null,
                                claim: attrs.claim || null
                            }
                        })
                        .run();
                    
                    return result;
                } 
                else if (attrs.blockType === 'boilerplate') {
                    
                    // Insert a new paragraph with the same boilerplate attributes
                    const result = editor
                        .chain()
                        .insertContent({
                            type: 'paragraph',
                            attrs: {
                                class: 'boilerplate-block',
                                blockType: 'boilerplate',
                                name: attrs.name,
                                // Ensure data-* attributes are properly set for HTML
                                'data-block-type': 'boilerplate',
                                'data-name': attrs.name
                            }
                        })
                        .run();
                    
                    return result;
                }
                else if (attrs.blockType === 'claim-assignment') {
                    
                    // Insert a new paragraph with the same claim-assignment attributes
                    const result = editor
                        .chain()
                        .insertContent({
                            type: 'paragraph',
                            attrs: {
                                class: 'editor-paragraph claim-assignment-paragraph',
                                blockType: 'claim-assignment'
                            }
                        })
                        .run();
                    
                    return result;
                }
                
                return false;
            },
            'Shift-Enter': ({ editor }) => {
                
                // Create a new normal paragraph without any special attributes
                const result = editor
                    .chain()
                    .insertContent({ 
                        type: 'paragraph',
                        attrs: { class: 'editor-paragraph' }
                    })
                    .run();
                
                return result;
            }
        }
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: paragraphNumberingPluginKey,
                props: {
                    decorations(state) {
                        const { doc } = state
                        const decorations = []
                        let paragraphCount = 0

                        // Find all paragraphs in the document
                        doc.descendants((node, pos, _parent, _index) => {
                            // Skip counting if this is a heading
                            if (node.type.name === 'heading') {
                                return
                            }

                            // Only increment count and add decoration for paragraphs (not other nodes)
                            if (node.type.name === 'paragraph') {
                                // Check if paragraph is empty
                                const isEmpty = node.content.size === 0 ||
                                    (node.textContent.trim() === '');

                                // Skip empty paragraphs
                                if (isEmpty) {
                                    return
                                }

                                // Skip abstract paragraphs - they don't get paragraph numbers
                                if (node.attrs.context === 'abstract') {
                                    return
                                }

                                // Check if this paragraph contains only the '/' character and block menu is open
                                const blockMenuState = get(blockMenuStore)
                                const isBlockMenuOpen = blockMenuState.isOpen
                                const isJustSlash = node.textContent.trim() === '/'

                                // Skip numbering if the block menu is being triggered
                                if (isBlockMenuOpen && isJustSlash) {
                                    return
                                }

                                // Increment paragraph count
                                paragraphCount++

                                // Format the number with leading zeros
                                const paddedNumber = paragraphCount.toString().padStart(4, '0')
                                
                                // Create a decoration with the paragraph number
                                // We handle regenerative blocks a bit differently
                                const isRegenerative = node.attrs.blockType === 'regenerative-support';
                                
                                // Create a custom decoration based on the block type
                                let decoration;
                                
                                if (isRegenerative) {
                                    // For regenerative blocks, just add a class that can be selected via CSS
                                    // and manually handle the paragraph number display
                                    decoration = Decoration.node(pos, pos + node.nodeSize, {
                                        class: 'regenerative-paragraph',
                                        'data-paragraph-number': `[${paddedNumber}]`
                                    }, { key: `para-regen-${paragraphCount}` });
                                } else if (node.attrs.blockType === 'boilerplate') {
                                    // For boilerplate blocks, handle similarly to regenerative blocks
                                    decoration = Decoration.node(pos, pos + node.nodeSize, {
                                        class: 'boilerplate-paragraph',
                                        'data-paragraph-number': `[${paddedNumber}]`
                                    }, { key: `para-boilerplate-${paragraphCount}` });
                                } else if (node.attrs.blockType === 'claim-assignment') {
                                    // For claim assignment blocks, handle similarly to other blocks
                                    decoration = Decoration.node(pos, pos + node.nodeSize, {
                                        class: 'claim-assignment-paragraph',
                                        'data-paragraph-number': `[${paddedNumber}]`
                                    }, { key: `para-claim-${paragraphCount}` });
                                } else {
                                    // For regular paragraphs, use the normal paragraph numbering
                                    decoration = Decoration.node(pos, pos + node.nodeSize, {
                                        class: 'numbered-paragraph',
                                        'data-paragraph-number': `[${paddedNumber}]`
                                    }, { key: `para-${paragraphCount}` });
                                }
                                
                                decorations.push(decoration)
                            }
                        })

                        return DecorationSet.create(doc, decorations)
                    }
                }
            })
        ]
    }
})